import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShoppingBag, Check, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { isPackageBasedProduct } from '@/lib/packageMeasurementUtils';
import { formatPrice, formatMeasurementUnit } from '@/lib/formatters';

interface ProductActionsProps {
    product: any;
    quantity: number;
    setQuantity: (q: number) => void;
    userQuantity: string;
    setUserQuantity: (q: string) => void;
    isInCart: boolean;
    cartItem?: any;
    handleAddToCart: () => void;
    flashQuoteButton?: boolean;
    isPackageMode: boolean;
    openCart: () => void;
}

export const ProductActions: React.FC<ProductActionsProps> = ({
    product,
    quantity,
    setQuantity,
    userQuantity,
    setUserQuantity,
    isInCart,
    cartItem,
    handleAddToCart,
    flashQuoteButton,
    isPackageMode,
    openCart
}) => {
    const step = product.step_quantity || 1;
    const min = product.min_quantity || 1;
    const max = product.max_quantity || 999999;

    const handleQuantityChange = (newVal: string) => {
        setUserQuantity(newVal);
        const val = parseInt(newVal);
        if (!isNaN(val) && val >= min) {
            setQuantity(val);
        }
    };

    const incrementQuantity = () => {
        const current = parseInt(userQuantity) || min;
        const next = current + step;
        if (next <= max) {
            setUserQuantity(next.toString());
            setQuantity(next);
        }
    };

    const decrementQuantity = () => {
        const current = parseInt(userQuantity) || min;
        const next = current - step;
        if (next >= min) {
            setUserQuantity(next.toString());
            setQuantity(next);
        }
    };

    const validateAndAddToCart = () => {
        // CRITICAL: Final Validation Check Before Adding to Cart
        const valToCheck = parseInt(userQuantity);

        // Calculate Maximum Allowed Quantity (Logic MUST match inputs)
        // Base Unit Inventory (e.g. SQM)
        const totalInventoryBase = product.total_inventory || 999999;
        const isPkgMode = isPackageBasedProduct(product.measurement_unit || '') && product.package_coverage_per_unit;
        // const coverage = isPkgMode ? product.package_coverage_per_unit || 1 : 1;

        // 1. Check Physical Inventory
        let maxByInventory = totalInventoryBase;
        // If not package mode, maxByInventory is just totalInventoryBase

        // 2. Liquidation Limit
        if (product.liquidation_sale_enabled) {
            const liquidationInv = product.liquidation_current_inventory !== undefined
                ? product.liquidation_current_inventory
                : totalInventoryBase;

            maxByInventory = liquidationInv;
        }

        // 3. Group Buy Limit
        if (product.group_buy_enabled) {
            const target = product.group_buy_target_quantity || product.target_quantity || 999999;
            const currentGroupQty = product.group_buy_current_quantity || product.current_quantity || 0;
            const remainingToTargetBase = Math.max(0, target - currentGroupQty);

            // Limit to target if enabled
            if (product.group_buy_limit_to_target && remainingToTargetBase > 0) {
                if (isPkgMode) {
                    // Logic from ProductDetail:
                    // if (isPkgMode) { maxByInventory = Math.min(maxByInventory, remainingToTargetBase); }
                    // Assuming target/current are compatible units or normalized
                    maxByInventory = Math.min(maxByInventory, remainingToTargetBase);
                } else {
                    maxByInventory = Math.min(maxByInventory, remainingToTargetBase);
                }
            }
        }

        const maxQty = Math.min(
            product.max_quantity || 999999,
            maxByInventory
        );

        if (valToCheck > maxQty) {
            toast.error(`שגיאה: הכמות המקסימלית להזמנה היא ${maxQty} ${isPkgMode ? 'חבילות' : formatMeasurementUnit(product.measurement_unit)}`);
            // Reset the input to the valid max
            setQuantity(maxQty);
            setUserQuantity(maxQty.toString());
            return;
        }

        if (isInCart) {
            const cartQtyChanged = (cartItem?.quantity || 0) !== parseInt(userQuantity);
            if (!cartQtyChanged) {
                openCart();
                return;
            }
        }

        handleAddToCart();
    };

    const isDisabled = (() => {
        // Group Buy Exclusion
        if (product.group_buy_enabled) return false;

        const totalBase = product.liquidation_sale_enabled
            ? (product.liquidation_current_inventory ?? product.total_inventory ?? 0)
            : (product.total_inventory ?? 999999);

        if (totalBase <= 0) return true;

        // Check min vs total
        return totalBase < min;
    })();

    const getButtonClasses = () => {
        // Group Buy Exclusion
        if (product.group_buy_enabled) {
            if (flashQuoteButton) return 'bg-red-500 hover:bg-red-600 scale-105';
            if (isInCart && (cartItem?.quantity || 0) === parseInt(userQuantity)) {
                return 'bg-gray-100 text-gray-800 border-2 border-gray-200 hover:bg-gray-200 shadow-none';
            }
            return 'bg-lime text-lime-foreground hover:bg-lime/90 shadow-lg shadow-lime/20';
        }

        const effectiveTotal = product.liquidation_sale_enabled
            ? (product.liquidation_current_inventory ?? 0)
            : (product.total_inventory ?? 0);

        if (effectiveTotal <= 0) {
            return 'bg-gray-400 hover:bg-gray-400 text-white cursor-not-allowed';
        }

        if (flashQuoteButton) return 'bg-red-500 hover:bg-red-600 scale-105';

        if (isInCart && (cartItem?.quantity || 0) === parseInt(userQuantity)) {
            return 'bg-gray-100 text-gray-800 border-2 border-gray-200 hover:bg-gray-200 shadow-none';
        }

        return 'bg-lime text-lime-foreground hover:bg-lime/90 shadow-lg shadow-lime/20';
    };

    return (
        <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center">
            {/* 2. Quantity Selector */}
            <div className="flex-1 w-full flex flex-col gap-3">
                <span className="text-sm text-muted-foreground text-center">כמות</span>
                <div className="flex items-center">
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-14 w-14 rounded-l-none border-l-0"
                        onClick={decrementQuantity}
                        disabled={isDisabled} // Disable if out of stock
                    >
                        <Minus className="h-4 w-4" />
                    </Button>
                    <div className="relative flex-1">
                        <Input
                            type="number"
                            min={min}
                            max={max}
                            step={step}
                            value={userQuantity}
                            onChange={(e) => handleQuantityChange(e.target.value)}
                            className="h-14 text-center text-lg font-bold rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
                            disabled={isDisabled}
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-14 w-14 rounded-r-none border-r-0"
                        onClick={incrementQuantity}
                        disabled={isDisabled}
                    >
                        <Plus className="h-4 w-4" />
                    </Button>
                </div>

                {/* Helper text for quantities */}
                <div className="text-xs text-muted-foreground text-center">
                    מלאי זמין: {
                        product.group_buy_enabled
                            ? ((product.group_buy_target_quantity || 0) - (product.group_buy_current_quantity || 0))
                            : (
                                product.liquidation_sale_enabled
                                    ? product.liquidation_current_inventory
                                    : (product.total_inventory || 999999)
                            )
                    } | כפולות: {step}
                </div>
            </div>

            {/* 3. Total & Action */}
            <div className="flex-1 w-full flex flex-col gap-3">
                <div className="flex flex-col items-center">
                    <span className="text-sm text-muted-foreground">סה"כ לתשלום</span>
                    <div className="text-4xl font-black text-lime tracking-tight">
                        {formatPrice(quantity * product.price_per_unit)}
                    </div>
                </div>

                <Button
                    size="lg"
                    disabled={isDisabled}
                    onClick={validateAndAddToCart}
                    className={`w-full text-lg font-bold h-14 mt-2 transition-all ${getButtonClasses()}`}
                >
                    {isInCart ? (
                        <>
                            <Check className="mr-2 h-6 w-6" />
                            {(cartItem?.quantity || 0) !== parseInt(userQuantity) ? 'עדכן כמות' : 'נוסף לעגלה'}
                        </>
                    ) : (
                        (() => {
                            // Group Buy Exclusion
                            if (product.group_buy_enabled) {
                                return (
                                    <>
                                        <ShoppingBag className="mr-2 h-6 w-6" />
                                        הוסף לעגלה
                                    </>
                                );
                            }

                            const totalBase = product.liquidation_sale_enabled
                                ? (product.liquidation_current_inventory ?? product.total_inventory ?? 0)
                                : (product.total_inventory ?? 0);

                            if (totalBase <= 0) {
                                return "אזל מהמלאי";
                            }

                            return (
                                <>
                                    <ShoppingBag className="mr-2 h-6 w-6" />
                                    הוסף לעגלה
                                </>
                            );
                        })()
                    )}
                </Button>
            </div>
        </div>
    );
};
